import { format } from 'date-fns';
import { connect } from 'formik';
import React, { Component, PropsWithChildren, createRef, RefObject } from 'react';
import { RegisterElement } from '..';
import { FieldHelpersWrapper } from '../utils/formHelpers';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { sizeStyles } from '../utils/sizes';
import DateRangeEditor from './DateRangeEditor';
import { IDateRangePickerProps, IDateType, InnerDateRangePickerProps, IRangeNames, IRangeValues } from './types';

import './DateRangePicker.scss';

/**
 *
 * todo: the string that appears in the readOnly input field needs to be localized
 *
 * DateRangePicker uses our DateTimePicker component
 *
 */
export class DateRangePicker extends Component<PropsWithChildren<InnerDateRangePickerProps>, { isEditing: boolean }> {
  public static defaultProps = {
    label: 'Select a date range',
    name: { start: '', end: '' },
    placeholder: 'start date - end date',
  };

  // returns a complete date range string or empty string
  public static toString(start: IDateType, end: IDateType): string {
    const formatDate = (DateRangeEditor as any).raw.format.date;
    return start && end ? `${format(start, formatDate)} - ${format(end, formatDate)}` : '';
  }

  public state = {
    isEditing: false,
  };
  private id = createId('date-range-picker');

  private drPicker: RefObject<HTMLDivElement>;

  constructor(props: InnerDateRangePickerProps) {
    super(props);
    this.drPicker = createRef();
  }

  public componentDidMount() {
    if (this.props.formik.registerField) {
      this.props.formik.registerField(this.props.name.start, this);
      this.props.formik.registerField(this.props.name.end, this);
      window.document.addEventListener('keydown', this.onEscape, true);
    }
  }

  public componentWillUnmount() {
    if (this.props.formik.unregisterField) {
      this.props.formik.unregisterField(this.props.name.start);
      this.props.formik.unregisterField(this.props.name.end);
      window.document.removeEventListener('keydown', this.onEscape, true);
    }
  }

  public updateFormik = (n: IRangeNames, value: IRangeValues) => {
    const { name, formik } = this.props;

    if (formik && formik.setFieldValue) {
      formik.setFieldValue(name.start, value.start);
      formik.setFieldValue(name.end, value.end);
    }
  };

  public handleChange = (name: IRangeNames, value: IRangeValues) => {
    const method = this.props.onChange || this.updateFormik;
    method(name, value);
    this.setState({ isEditing: false });
    this.drPicker.current!.querySelector('input')!.focus();
  };

  public handleCancel = () => {
    this.setState({
      isEditing: false,
    });
  };

  // when <Enter> key is pressed in the readonly input field, toggle the range editor visibility
  // added for accessibility
  // handleEnterKey = (e: KeyboardEventHandler<>) => {
  public handleEnterKey = (e: any) => {
    // tslint:disable-line
    if (e.keyCode === 13) {
      this.toggleEdit();
    }
  };

  public toggleEdit = () => {
    this.setState({ isEditing: !this.state.isEditing });
  };

  /**
   * returns the date object or null for the chosen range interval that is currently set
   * if component has formik context then return the corresponding formik value
   * otherwise return appropriate value from props
   */
  public getCurrentDate(interval: 'start' | 'end'): IDateType {
    const { name, value = { start: null, end: null }, formik } = this.props;
    const isStart = interval === 'start';
    const getName = isStart ? name.start || '' : name.end || '';
    const getValue = isStart ? value.start : value.end;
    let date: IDateType;
    if (getValue) {
      date = getValue;
    } else if (Object.keys(formik).length && formik.values[getName] !== undefined) {
      date = formik.values[getName];
    } else {
      date = null;
    }

    return date;
  }

  public maybeEditing() {
    if (this.state.isEditing) {
      const { onChange, placeholder, label, value, ...props } = this.props;
      return (
        <DateRangeEditor
          value={{
            end: this.getCurrentDate('end'),
            start: this.getCurrentDate('start'),
          }}
          onCancel={this.handleCancel}
          onChange={this.handleChange}
          {...props}
        />
      );
    } else {
      return null;
    }
  }
  public maybeLabel() {
    const { label } = this.props;
    return label ? <label className={'control-label'}>{label}</label> : null;
  }

  public rangeToString(): string {
    const { placeholder } = this.props;
    const startDate = this.getCurrentDate('start');
    const endDate = this.getCurrentDate('end');

    if (startDate && endDate) {
      return DateRangePicker.toString(startDate, endDate);
    } else {
      return placeholder || '';
    }
  }

  public render() {
    const { id } = this;
    const {
      valid = true,
      touched = false,
      onBlur,
      sizePreset,
    } = resolveProps({ ...this.props, name: this.props.name.start }, this.props.formik);
    const showError = touched && !valid;

    return (
      <RegisterElement name={`register-${id}`}>
        {(ref: any) => {
          const myRef = showError ? ref : undefined;
          return (
            <div id={`${id}-wrapper`} ref={myRef}>
              <div
                className="hlc-date-rangepicker"
                qa-id={this.props['qa-id']}
                ref={this.drPicker}
                style={sizeStyles(sizePreset)}
              >
                <FieldHelpersWrapper
                  id={id}
                  props={
                    Object.assign({}, this.props, {
                      name: this.props.name.start,
                    }) as any
                  }
                >
                  <div className={showError ? 'has-error' : undefined}>
                    <div className="hlc-drp-summary" onClick={this.toggleEdit}>
                      <input
                        id={id}
                        name={this.props.name.start}
                        value={this.rangeToString()}
                        readOnly={true}
                        onKeyDownCapture={this.handleEnterKey}
                        onBlur={onBlur}
                      />
                      {/* using the react-widgets calendar icon instead of our fontawesome calendar icon so they all match */}
                      <span aria-hidden={'true'} className={'rw-i rw-i-calendar'} title={'Select date range'} />
                    </div>
                    {this.maybeEditing()}
                  </div>
                </FieldHelpersWrapper>
              </div>
            </div>
          );
        }}
      </RegisterElement>
    );
  }
  private onEscape = (e: KeyboardEvent) => {
    if (e.keyCode === 27 && this.state.isEditing) {
      if (this.drPicker.current && !this.drPicker.current.querySelector('.hlc-datetimepicker-opendate')) {
        this.handleCancel();
        e.stopPropagation();
      }
    }
  };
}

export default connect<IDateRangePickerProps>(DateRangePicker);
